import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useStartEventAgentRun } from '../api/useEventAgent';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Called with the new run id once the agent run is dispatched. */
  onRunStarted: (runId: string) => void;
}

/**
 * "Auto-Map Event" — type a prediction-market event; the event-map agent
 * scrapes Kalshi + Polymarket and creates the pairs. Mirrors ManualPairDialog.
 */
export const AutoMapEventDialog = ({ open, onClose, onRunStarted }: Props) => {
  const [eventQuery, setEventQuery] = useState('');
  const [kalshiUrl, setKalshiUrl] = useState('');
  const [polymarketUrl, setPolymarketUrl] = useState('');
  const startRun = useStartEventAgentRun();

  const reset = () => {
    setEventQuery('');
    setKalshiUrl('');
    setPolymarketUrl('');
    startRun.reset();
  };

  const handleClose = () => {
    if (startRun.isLoading) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    const q = eventQuery.trim();
    if (!q) return;
    const run = await startRun.mutateAsync({
      eventQuery: q,
      kalshiUrl: kalshiUrl.trim() || undefined,
      polymarketUrl: polymarketUrl.trim() || undefined,
    });
    reset();
    onClose();
    onRunStarted(run._id);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Auto-Map Event</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            The agent scrapes every market for this event on Kalshi + Polymarket,
            creates the cross-platform pairs, and reports back which markets it
            could not pair. Describe the event, and optionally paste the Kalshi
            and/or Polymarket event URLs so the agent can find it faster.
          </Typography>
          <TextField
            label="Event"
            placeholder="e.g. Cavaliers vs Knicks May 19"
            value={eventQuery}
            onChange={(e) => setEventQuery(e.target.value)}
            onKeyDown={(e) => {
              // Multiline: Enter inserts a newline; submit on Cmd/Ctrl+Enter.
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
            }}
            autoFocus
            fullWidth
            multiline
            minRows={4}
          />
          <TextField
            label="Kalshi URL (optional)"
            placeholder="https://kalshi.com/markets/…"
            value={kalshiUrl}
            onChange={(e) => setKalshiUrl(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Polymarket URL (optional)"
            placeholder="https://polymarket.com/event/…"
            value={polymarketUrl}
            onChange={(e) => setPolymarketUrl(e.target.value)}
            fullWidth
            size="small"
          />
          {startRun.isError && (
            <Alert severity="error">
              Failed to start the run. Check that prediction-manager can
              dispatch the agent Job.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={startRun.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!eventQuery.trim() || startRun.isLoading}
          startIcon={
            startRun.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          {startRun.isLoading ? 'Starting…' : 'Map Event'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
